import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useProfileStore } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';
import { Trash2, AlertTriangle, Check, ChevronDown, MapPin } from 'lucide-react';
import CurrencySelector from '../components/CurrencySelector';
import { countries, type Country } from '../data/countries';
import FlagIcon from '../components/FlagIcon';

// Predefined avatar options
const AVATAR_OPTIONS = [
  '/avatars/char.png',
  '/avatars/bulba.png',
  '/avatars/squir.png'
];

interface Profile {
  id: string;
  username: string | null;
  avatar_url: string | null;
  email: string | null;
  country: string | null;
  country_code: string | null;
  auth_provider: string | null;
  created_at: string | null;
  updated_at: string | null;
  last_sign_in: string | null;
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [username, setUsername] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [showAvatarSelector, setShowAvatarSelector] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const navigate = useNavigate();
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const setGlobalProfile = useProfileStore(state => state.setProfile);
  const [currentCountry, setCurrentCountry] = useState<string | null>(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countryLoading, setCountryLoading] = useState(false);
  const [highlightedCountryIndex, setHighlightedCountryIndex] = useState(-1);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) {
        navigate('/auth');
        return;
      }
      setUser(session.user);
      getProfile(session.user.id, session.user).finally(() => {
        setInitialized(true);
      });
    });
  }, [navigate]);

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownOpen) {
        const target = event.target as Element;
        if (!target.closest('.country-dropdown')) {
          setCountryDropdownOpen(false);
        }
      }
    };

    if (countryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [countryDropdownOpen]);

  // Handle keyboard navigation for country dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!countryDropdownOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedCountryIndex(prev => 
            prev < countries.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedCountryIndex(prev => 
            prev > 0 ? prev - 1 : countries.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (highlightedCountryIndex >= 0 && countries[highlightedCountryIndex]) {
            handleCountryChange(countries[highlightedCountryIndex]);
          }
          break;
        case 'Escape':
          setCountryDropdownOpen(false);
          setHighlightedCountryIndex(-1);
          break;
        default:
          // Handle letter navigation (jump to first country starting with that letter)
          if (e.key.length === 1 && e.key.match(/[a-zA-Z]/)) {
            const letter = e.key.toLowerCase();
            const index = countries.findIndex(country =>
              country.name.toLowerCase().startsWith(letter)
            );
            if (index >= 0) {
              setHighlightedCountryIndex(index);
              // Scroll to the highlighted item
              setTimeout(() => {
                const element = document.querySelector(`[data-country-index="${index}"]`);
                element?.scrollIntoView({
                  block: 'nearest',
                  behavior: 'smooth'
                });
              }, 0);
            }
          }
          break;
      }
    };

    if (countryDropdownOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [countryDropdownOpen, highlightedCountryIndex]);

  // Reset highlighted index when dropdown opens
  useEffect(() => {
    if (countryDropdownOpen) {
      setHighlightedCountryIndex(-1);
    }
  }, [countryDropdownOpen]);

  async function getProfile(userId: string, userObj?: User) {
    try {
      let { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // If profile doesn't exist, try to create it using the ensure_profile_exists function
      if (profileError && profileError.code === 'PGRST116') {
        console.log('Profile not found, attempting to create one...');
        
        try {
          const { data: createdProfile, error: createError } = await supabase
            .rpc('ensure_profile_exists', { user_id: userId });

          if (createError) {
            console.error('Error creating profile:', createError);
          } else if (createdProfile) {
            profile = createdProfile;
            profileError = null;
          }
        } catch (createError) {
          console.error('Failed to create profile:', createError);
        }
      }

      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }

      if (profile) {
        setProfile(profile);
        setUsername(profile.username || '');
        setAuthProvider(profile.auth_provider || null);
        const currentAvatar = profile.avatar_url;
        setAvatarUrl(currentAvatar);
        setSelectedAvatar(currentAvatar);
        setGlobalProfile(profile);
        setHasUnsavedChanges(false);
        
        // Set country from profile or user metadata
        const countryCode = profile.country_code || (userObj || user)?.user_metadata?.country_code;
        setCurrentCountry(countryCode || null);
      } else {
        // If still no profile, create a minimal one
        console.log('Creating minimal profile...');
        const minimalProfile = {
          id: userId,
          username: (userObj || user)?.email?.split('@')[0] || '',
          avatar_url: null,
          email: (userObj || user)?.email || null,
          country: null,
          country_code: (userObj || user)?.user_metadata?.country_code || null,
          auth_provider: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          last_sign_in: null,
        };
        setProfile(minimalProfile);
        setUsername(minimalProfile.username);
        setCurrentCountry(minimalProfile.country_code);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    setHasUnsavedChanges(true);
  };

  const handleCountryChange = async (country: Country) => {
    if (!user) return;
    
    try {
      setCountryLoading(true);
      setError(null);

      // Update auth user metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          country_code: country.code,
          country: country.name,
        }
      });

      if (authError) throw authError;

      // Update profile with country info, preserving existing data
      // First ensure profile exists
      if (!profile) {
        try {
          const { data: createdProfile, error: createError } = await supabase
            .rpc('ensure_profile_exists', { user_id: user.id });

          if (createError) {
            console.error('Error ensuring profile exists:', createError);
          } else if (createdProfile) {
            setProfile(createdProfile);
          }
        } catch (createError) {
          console.error('Failed to ensure profile exists:', createError);
        }
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          country_code: country.code,
          country: country.name,
          updated_at: new Date().toISOString(),
          // Preserve existing data if profile exists
          ...(profile && {
            username: profile.username,
            email: profile.email,
            avatar_url: profile.avatar_url,
            auth_provider: profile.auth_provider,
            created_at: profile.created_at,
            last_sign_in: profile.last_sign_in,
          })
        });

      if (profileError) throw profileError;

      setCurrentCountry(country.code);
      setCountryDropdownOpen(false);
      
      // Update the local profile state
      if (profile) {
        const updatedProfile = {
          ...profile,
          country_code: country.code,
          country: country.name,
          updated_at: new Date().toISOString(),
        };
        setProfile(updatedProfile);
        setGlobalProfile(updatedProfile);
      }
      
      // Show success message briefly
      setError(null);
    } catch (error) {
      console.error('Error updating country:', error);
      setError(error instanceof Error ? error.message : 'Error updating country');
    } finally {
      setCountryLoading(false);
    }
  };

  async function updateAvatar() {
    if (!selectedAvatar) return;
    
    try {
      setError(null);
      setLoading(true);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: selectedAvatar })
        .eq('id', user?.id);

      if (updateError) throw updateError;

      setAvatarUrl(selectedAvatar);
      setGlobalProfile({ ...profile!, avatar_url: selectedAvatar });
      setHasUnsavedChanges(false);
      setShowAvatarSelector(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error uploading avatar');
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile() {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);

      const { error } = await supabase
        .from('profiles')
        .update({ username })
        .eq('id', user.id);

      if (error) throw error;
      setHasUnsavedChanges(false);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function deleteAccount() {
    if (!user || deleteConfirmText !== user.email) return;
    
    try {
      setLoading(true);
      setError(null);

      // Delete user account
      const { error } = await supabase.auth.admin.deleteUser(user.id);

      if (error) throw error;

      // Sign out and redirect
      await supabase.auth.signOut();
      navigate('/');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Error deleting account');
    } finally {
      setLoading(false);
    }
  }

  if (loading && !initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Profile Settings</h1>

        <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-8">
          {/* Avatar Section */}
          <div className="flex items-center space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white/10 overflow-hidden border-2 border-white/10">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40">
                    No Image
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowAvatarSelector(true)}
                className="absolute bottom-0 right-0 p-2 bg-white/10 rounded-full cursor-pointer hover:bg-white/20 transition-colors text-white group"
              >
                <span className="hidden group-hover:block">Change</span>
                <span className="block group-hover:hidden">Edit</span>
              </button>
            </div>
            <div>
              <h3 className="text-lg font-medium">Profile Picture</h3>
              <p className="text-sm text-white/60">
                Choose your avatar from our collection
              </p>
            </div>
          </div>

          {/* Avatar Selector Modal */}
          {showAvatarSelector && (
            <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
              <div className="bg-white/5 border border-white/10 rounded-lg p-6 max-w-md w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium">Choose an Avatar</h3>
                  <button
                    onClick={() => {
                      setShowAvatarSelector(false);
                      setSelectedAvatar(avatarUrl);
                    }}
                    className="text-white/60 hover:text-white"
                  >
                    Close
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {AVATAR_OPTIONS.map((url, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedAvatar(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-105 ${
                        selectedAvatar === url
                          ? 'border-white ring-2 ring-white/25'
                          : 'border-white/10 hover:border-white/25'
                      }`}
                    >
                      <img
                        src={url}
                        alt={`Pokemon avatar ${url.split('/').pop()?.split('.')[0]}`}
                        className="w-full h-full object-cover"
                      />
                      {selectedAvatar === url && (
                        <div className="absolute inset-0 bg-white/10 flex items-center justify-center">
                          <Check className="text-white" size={24} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={updateAvatar}
                    disabled={!selectedAvatar || selectedAvatar === avatarUrl || loading}
                    className="flex-1 py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Saving...' : 'Save Avatar'}
                  </button>
                  <button
                    onClick={() => {
                      setShowAvatarSelector(false);
                      setSelectedAvatar(avatarUrl);
                    }}
                    className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Profile Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium mb-3">Currency Preference</h3>
              <p className="text-sm text-white/60 mb-4">
                Choose your preferred currency for card prices.
              </p>
              <CurrencySelector />
            </div>

            {/* Country Selection */}
            <div>
              <h3 className="text-lg font-medium mb-3">Country</h3>
              <p className="text-sm text-white/60 mb-4">
                Your country helps us provide region-appropriate content and comply with local regulations.
              </p>
              <div className="relative country-dropdown">
                <button
                  type="button"
                  onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                  disabled={countryLoading}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/25 flex items-center justify-between hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  <span className="flex items-center gap-3">
                    {currentCountry ? (
                      <>
                        <FlagIcon countryCode={currentCountry} size="md" />
                        <span>{countries.find(c => c.code === currentCountry)?.name || 'Unknown Country'}</span>
                      </>
                    ) : (
                      <span className="text-white/60">Select your country</span>
                    )}
                  </span>
                  <ChevronDown 
                    size={20} 
                    className={`transform transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                
                {countryDropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-black/95 border border-white/10 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                    {/* Keyboard navigation hint */}
                    <div className="px-4 py-2 border-b border-white/10 text-xs text-white/60">
                      💡 Press any letter to jump to countries (e.g., 'U' for United States)
                    </div>
                    
                    {countries.map((country, index) => (
                      <button
                        key={country.code}
                        type="button"
                        data-country-index={index}
                        onClick={() => handleCountryChange(country)}
                        disabled={countryLoading}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 text-white transition-colors border-b border-white/5 last:border-b-0 disabled:opacity-50 ${
                          index === highlightedCountryIndex 
                            ? 'bg-white/20' 
                            : 'hover:bg-white/10'
                        }`}
                      >
                        <FlagIcon countryCode={country.code} size="md" />
                        <span>{country.name}</span>
                        <span className="text-xs text-white/40 ml-auto">{country.code}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {countryLoading && (
                <div className="mt-2 text-sm text-blue-400">
                  Updating country...
                </div>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/60 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white/60"
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-white/60 mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={handleUsernameChange}
                className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-white/25"
              />
            </div>

            {hasUnsavedChanges && (
              <div className="text-yellow-500 text-sm bg-yellow-500/10 p-3 rounded-lg">
                You have unsaved changes
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              onClick={updateProfile}
              disabled={loading || !hasUnsavedChanges}
              className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'No Changes'}
            </button>
          </div>

          {/* Authentication Provider Info */}
          <div className="mt-4 p-4 bg-white/5 rounded-lg">
            <h3 className="text-lg font-medium mb-2">Account Details</h3>
            <div className="space-y-2">
              <p className="text-white/60">
                Sign in method:{' '}
                <span className="text-white">
                  {authProvider === 'discord' ? 'Discord' :
                   authProvider === 'google' ? 'Google' :
                   authProvider === 'email' ? 'Email and Password' :
                   'Unknown'}
                </span>
              </p>
              <p className="text-white/60">
                Account created: {profile?.created_at && (
                  <span className="text-white">
                    {new Date(profile.created_at).toLocaleDateString()}
                  </span>
                )}
              </p>
              <p className="text-white/60">
                Last sign in: {profile?.last_sign_in && (
                  <span className="text-white">
                    {new Date(profile.last_sign_in).toLocaleDateString()}
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Delete Account Section */}
          <div className="pt-8 border-t border-white/10">
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Trash2 className="text-red-500 mt-1" size={20} />
                <div>
                  <h3 className="text-lg font-medium text-red-500">Delete Account</h3>
                  <p className="text-sm text-white/60">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                </div>
              </div>

              {showDeleteConfirm ? (
                <div className="space-y-4">
                  <div className="bg-red-500/10 p-4 rounded-lg flex items-start space-x-3">
                    <AlertTriangle className="text-red-500 mt-1" size={20} />
                    <div>
                      <p className="text-sm text-red-500">
                        This action is permanent and cannot be undone. Please type your email address
                        (<strong>{user?.email}</strong>) to confirm deletion.
                      </p>
                    </div>
                  </div>

                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Enter your email to confirm"
                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-red-500/25 text-white focus:outline-none focus:ring-2 focus:ring-red-500/25"
                  />

                  <div className="flex space-x-3">
                    <button
                      onClick={deleteAccount}
                      disabled={deleteConfirmText !== user?.email}
                      className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm Delete
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteConfirmText('');
                      }}
                      className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white font-medium transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-4 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-500 font-medium transition-colors"
                >
                  Delete Account
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}